import { GROUPED_TRANSFORM_RULES } from 'js/constants/transforms';
import TransformDSLParser, { StringLiteral } from './TransformDSLParser';

export const getCustomTxRule = (funcName: string) => {
  const customTxId = getCustomTxId(funcName);

  return {
    customTxId,
    value: `customAttrTx_${customTxId}`,
    label: `Tx_${customTxId}`,
    group: 'miscGroup',
    help: {
      desc: '-',
      ex: '-',
    },
    ignoreSrc: true,
    requiredParams: ['attrTxId'],
    params: [
      {
        name: 'attrTxId',
        display_name: 'Transform ID',
        desc: 'ID of the custom transform',
        typesAllowed: ['number'],
        inputElement: 'text',
        format: 'string',
      },
    ],
  };
};

export const getCustomTxId = (funcName: string) => {
  // Match: Tx_12345 (standard DSL format with capital T and underscore)
  if (/^Tx_\d+$/.test(funcName)) {
    return parseInt(funcName.substring('Tx_'.length));
  }

  // Match: tx12345 (lowercase without underscore)
  if (/^tx\d+$/.test(funcName)) {
    return parseInt(funcName.substring('tx'.length));
  }

  // Match: customAttrTx_12345 (internal format)
  if (/^customAttrTx_\d+$/.test(funcName)) {
    return parseInt(funcName.substring('customAttrTx_'.length));
  }

  return null;
};

export const extractDestAttr = (statement: string) => {
  // remove all strings (which may contain escaped quotes inside)
  const cleanInput = statement.replace(new RegExp(StringLiteral.PATTERN as RegExp, 'g'), ' ');

  // remove dest attr
  const eqIndex = cleanInput.lastIndexOf('=');
  let destAttr = null;
  let funcCallStr = statement;

  if (eqIndex !== -1) {
    destAttr = statement.substring(0, eqIndex).trim();
    funcCallStr = ' '.repeat(eqIndex + 1) + statement.substring(eqIndex + 1).trim();
  }

  return { destAttr, funcCallStr };
};

export const normalizeFuncName = (funcName: string) => {
  return funcName.replace(/[\s\n_]/g, '').toLowerCase();
};

export const getFuncRule = (funcName: string, rulesByName: any = null) => {
  const normalizedFuncName = normalizeFuncName(funcName);

  if (rulesByName) {
    const rulesByNormalizedName = Object.values(rulesByName).reduce((acc, rule) => {
      acc[normalizeFuncName((rule as any).label)] = rule;
      return acc;
    }, {});

    return rulesByNormalizedName[normalizedFuncName];
  }

  const isCustomTx = !!getCustomTxId(normalizedFuncName);

  return isCustomTx ? getCustomTxRule(normalizedFuncName) : rulesByLabel[normalizedFuncName];
};

export const getParamDef = (funcRule: any, index: number) => {
  if (funcRule.customTxId) {
    return null;
  }

  if (index === 0 && funcRule.__srcAttr && !funcRule.ignoreSrc) {
    return funcRule.__srcAttr;
  }

  const funcParams = [...(funcRule.params || []), ...(funcRule.optionalParams || [])];
  const paramIndex = index - (funcRule.__srcAttr && !funcRule.ignoreSrc ? 1 : 0);

  // if the function has a rest param, and we're at the end of the params,
  // return the rest param
  if (
    funcParams[funcParams.length - 1]?.inputElement === 'restParams' &&
    paramIndex >= funcParams.length - 1
  ) {
    return funcParams[funcParams.length - 1];
  }

  return funcParams[paramIndex];
};

export function parseDSLCodeArray(dslCode: string[]) {
  if (!dslCode || dslCode.length === 0) {
    return [];
  }

  return dslCode.map((code: string) => {
    const parsedDSL = TransformDSLParser.parse(code);
    return {
      code,
      __id: window?.crypto?.randomUUID(),
      ...parsedDSL,
    };
  });
}

export interface TransformDSLContext {
  inString: boolean;
  inAttrRef: boolean;
  inMetadataRef: boolean;
  currentFunc: string | null;
  currentArgIndex: number;
  tokenStack: string[];
  destAttr: string | null;
}

/**
 * Analyzes the code up to a given position to determine the context.
 * This includes detecting if we're inside a string literal, attribute
 * reference, metadata reference, or function call, and tracking the current
 * function and argument index.
 */
export const getContextAt = (content: string, position: number): TransformDSLContext => {
  const { destAttr, funcCallStr } = extractDestAttr(content);
  const normalized = funcCallStr.replace(/[\s\n]/g, ' ');

  let tokenStack: string[] = [];
  let inString = false;
  let inAttrRef = false;
  let inMetadataRef = false;
  let currentFunc = null;
  let currentArgIndex = -1;
  let lastQuote = null;

  for (let i = 0; i < position; i++) {
    const char = normalized[i];

    // Handle string literals
    if (char === '"' || char === "'") {
      // Check if this quote is escaped
      const isEscaped = normalized[i - 1] === '\\';

      if (!isEscaped) {
        if (lastQuote === char) {
          tokenStack.pop();
          lastQuote = null;
          inString = tokenStack.some((token) => token === '"' || token === "'");
        } else {
          tokenStack.push(char);
          lastQuote = char;
          inString = true;
        }
      }
      continue;
    }

    if (inString) continue;

    // Handle attribute references
    if (char === '$' && normalized[i + 1] === '(') {
      inAttrRef = true;
      i++; // Skip the opening parenthesis
      continue;
    }

    if (inAttrRef && char === ')') {
      inAttrRef = false;
      continue;
    }

    // Handle metadata references
    if (char === '$' && normalized[i + 1] === '$' && normalized[i + 2] === '(') {
      inMetadataRef = true;
      i += 2; // Skip the opening parenthesis
      continue;
    }

    if (inMetadataRef && char === ')') {
      inMetadataRef = false;
      continue;
    }

    // Handle function calls
    if (char === '(' && !inAttrRef && !inMetadataRef) {
      // Extract function name before the parenthesis
      const funcMatch = normalized.substring(0, i).match(/[a-zA-Z_][\w\s\n]*$/);
      if (funcMatch) {
        currentFunc = funcMatch[0].trim();
        currentArgIndex = 0;
      }
      tokenStack.push(`${currentFunc}(`);
      continue;
    }

    if (char === ')' && !inAttrRef && !inMetadataRef) {
      tokenStack.pop();

      const lastToken = tokenStack[tokenStack.length - 1];

      if (tokenStack.length === 0) {
        currentFunc = null;
        currentArgIndex = -1;
      } else if (lastToken.endsWith('(')) {
        currentFunc = lastToken.slice(0, -1);
      }

      continue;
    }

    // Handle argument separators
    if (char === ',' && !inAttrRef && !inMetadataRef && currentFunc) {
      currentArgIndex++;
    }
  }

  // Check if we're still in a string at the end
  inString = tokenStack.some((token) => token === '"' || token === "'");

  return {
    inString,
    inAttrRef,
    inMetadataRef,
    currentFunc,
    currentArgIndex,
    tokenStack,
    destAttr,
  };
};

export const rulesByLabel = Object.values(GROUPED_TRANSFORM_RULES.rules).reduce((acc, rule) => {
  acc[normalizeFuncName(rule.label)] = rule;
  return acc;
}, {});
